/**
 * WorkspaceNextMovePane — Read-only next move trajectory inspector
 *
 * Surfaces next move entries with proposalRefs, trajectoryRefs,
 * continuityRefs, admissionRefs, and reasonCodes.
 *
 * Shell B migration v1: migrates 'next-move' panel from UnifiedWorkspace
 * MockGovernancePanels into OperationalPaneModel shape. The mock panel
 * showed a hardcoded list. This pane shows real refs from the pane model.
 *
 * Real data source: bestNextMoveEngine (src/trustgate/modules/bestNextMove/)
 * No live integration required in this ratchet — pane contract only.
 *
 * SAFETY: Read-only, proof-only. No execution authority, no mutation.
 * Display of next move proposals does NOT constitute recommendation,
 * authorization, or instruction to act. Human review required before
 * any action is taken based on displayed information.
 */

import React from 'react';
import type { NextMovePaneModel, NextMoveEntry } from '../../modules/governanceEventStream/operationalRendering/types.js';
import { NEUTRAL, SPACING, TYPOGRAPHY, SEMANTIC } from '../../modules/governanceEventStream/operationalVisualLanguage/index.js';

interface WorkspaceNextMovePaneProps {
  model: NextMovePaneModel;
  onSelectEntry?: (entryId: string) => void;
}

function RefPill({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', gap: SPACING.sp2, alignItems: 'center' }}>
      <span style={{
        fontSize: TYPOGRAPHY.micro.size,
        color: NEUTRAL.text300,
        minWidth: 68,
        flexShrink: 0,
      }}>
        {label}
      </span>
      <span style={{
        fontSize: TYPOGRAPHY.micro.size,
        fontFamily: TYPOGRAPHY.mono.family,
        color: NEUTRAL.text400,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}>
        {value}
      </span>
    </div>
  );
}

function ReasonCodePill({ code }: { code: string }) {
  return (
    <span style={{
      fontSize: TYPOGRAPHY.micro.size,
      color: NEUTRAL.text400,
      backgroundColor: NEUTRAL.bg600,
      border: `1px solid ${NEUTRAL.borderSubtle}`,
      padding: '1px 5px',
      borderRadius: 2,
    }}>
      {code}
    </span>
  );
}

function NextMoveRow({ entry, onSelect }: { entry: NextMoveEntry; onSelect?: (id: string) => void }) {
  return (
    <div
      onClick={() => onSelect?.(entry.entryId)}
      style={{
        padding: `${SPACING.sp2}px`,
        backgroundColor: entry.isSelected ? NEUTRAL.bg500 : NEUTRAL.bg700,
        borderRadius: 3,
        borderLeft: `2px solid ${entry.isSelected ? SEMANTIC.neutral.border : 'transparent'}`,
        cursor: onSelect ? 'pointer' : 'default',
        marginBottom: SPACING.sp2,
      }}
      data-testid={`next-move-entry-${entry.entryId}`}
    >
      {/* Domain + type row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: SPACING.sp1 }}>
        {entry.domain && (
          <span style={{ fontSize: TYPOGRAPHY.micro.size, fontWeight: 600, color: NEUTRAL.text500 }}>
            {entry.domain}{entry.unitType ? ` · ${entry.unitType}` : ''}
          </span>
        )}
        {entry.estimatedScope && (
          <span style={{ fontSize: TYPOGRAPHY.micro.size, color: NEUTRAL.text300 }}>
            scope: {entry.estimatedScope}
          </span>
        )}
      </div>

      {/* Refs */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {entry.proposalRef && <RefPill label="proposal" value={entry.proposalRef} />}
        {entry.trajectoryRef && <RefPill label="trajectory" value={entry.trajectoryRef} />}
        {entry.continuityRef && <RefPill label="continuity" value={entry.continuityRef} />}
        {entry.admissionRef && <RefPill label="admission" value={entry.admissionRef} />}
      </div>

      {/* Reason codes */}
      {entry.reasonCodes.length > 0 && (
        <div style={{ display: 'flex', gap: SPACING.sp1, flexWrap: 'wrap', marginTop: SPACING.sp1 }}>
          {entry.reasonCodes.map((code, i) => <ReasonCodePill key={i} code={code} />)}
        </div>
      )}
    </div>
  );
}

export const WorkspaceNextMovePane: React.FC<WorkspaceNextMovePaneProps> = ({ model, onSelectEntry }) => {
  if (model.error) {
    return (
      <div style={{ padding: SPACING.sp6, color: NEUTRAL.text300, fontSize: TYPOGRAPHY.body.size }}>
        Next move pane error: {model.error}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>

      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: `${SPACING.sp2}px ${SPACING.sp3}px`,
        borderBottom: `1px solid ${NEUTRAL.border}`,
        backgroundColor: NEUTRAL.bg600,
        flexShrink: 0,
      }}>
        <span style={{
          fontSize: TYPOGRAPHY.heading.size,
          fontWeight: TYPOGRAPHY.heading.weight,
          color: NEUTRAL.text600,
        }}>
          Next Move
        </span>
        <span style={{ fontSize: TYPOGRAPHY.micro.size, color: NEUTRAL.text300 }}>
          {model.totalEntries} entries
        </span>
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflowY: 'auto', padding: SPACING.sp3 }}>

        {/* Safety strip */}
        <div style={{
          display: 'flex',
          gap: SPACING.sp2,
          flexWrap: 'wrap',
          marginBottom: SPACING.sp3,
          paddingBottom: SPACING.sp2,
          borderBottom: `1px solid ${NEUTRAL.borderSubtle}`,
        }}>
          {['READ-ONLY', 'PROOF-ONLY', 'NO EXECUTION AUTHORITY'].map(label => (
            <span key={label} style={{
              fontSize: TYPOGRAPHY.micro.size,
              fontWeight: 600,
              color: SEMANTIC.stabilized.text,
              backgroundColor: SEMANTIC.stabilized.chip,
              padding: '2px 6px',
              borderRadius: 3,
              textTransform: 'uppercase' as const,
              letterSpacing: 0.3,
            }}>
              {label}
            </span>
          ))}
          <span style={{
            fontSize: TYPOGRAPHY.micro.size,
            fontWeight: 600,
            color: NEUTRAL.text400,
            backgroundColor: NEUTRAL.bg500,
            padding: '2px 6px',
            borderRadius: 3,
            textTransform: 'uppercase' as const,
            letterSpacing: 0.3,
          }}>
            DISPLAY ONLY — NOT A RECOMMENDATION
          </span>
        </div>

        {/* Ref summary */}
        {(model.proposalRefs.length > 0 || model.trajectoryRefs.length > 0 || model.admissionRefs.length > 0) && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: SPACING.sp2,
            marginBottom: SPACING.sp3,
          }}>
            {[
              { label: 'Proposals', count: model.proposalRefs.length },
              { label: 'Trajectories', count: model.trajectoryRefs.length },
              { label: 'Admissions', count: model.admissionRefs.length },
            ].map(stat => (
              <div key={stat.label} style={{
                backgroundColor: NEUTRAL.bg600,
                border: `1px solid ${NEUTRAL.borderSubtle}`,
                borderRadius: 2,
                padding: `${SPACING.sp2}px`,
                textAlign: 'center' as const,
              }}>
                <div style={{ fontSize: TYPOGRAPHY.micro.size, color: NEUTRAL.text400, marginBottom: 2 }}>{stat.label}</div>
                <div style={{ fontSize: TYPOGRAPHY.label.size, fontWeight: 600, color: NEUTRAL.text500 }}>{stat.count}</div>
              </div>
            ))}
          </div>
        )}

        {/* Entry list */}
        <div style={{
          fontSize: TYPOGRAPHY.micro.size,
          fontWeight: 600,
          color: NEUTRAL.text500,
          textTransform: 'uppercase' as const,
          letterSpacing: 0.4,
          marginBottom: SPACING.sp2,
        }}>
          Entries ({model.entries.length})
        </div>

        {model.entries.length === 0 ? (
          <div style={{ padding: SPACING.sp6, color: NEUTRAL.text300, textAlign: 'center' as const }}>
            No next move entries
          </div>
        ) : (
          <div>
            {model.entries.map(entry => (
              <NextMoveRow key={entry.entryId} entry={entry} onSelect={onSelectEntry} />
            ))}
          </div>
        )}

      </div>
    </div>
  );
};
